import { MediaItem } from '../types';
import { normalizeRemoteUrl } from './fetchJson';

/**
 * Stream & Media Format Utilities
 * Handles format detection (MKV, MP4, HLS) and ExoPlayer integrations for Android TV & Web
 */

export function isMkvStream(url?: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const cleanUrl = url.trim().toLowerCase();
  
  // Check extension before query params or in URL path
  try {
    const parsed = new URL(cleanUrl);
    if (parsed.pathname.endsWith('.mkv')) return true;
    if (parsed.searchParams.get('format')?.toLowerCase() === 'mkv') return true;
    if (parsed.searchParams.get('ext')?.toLowerCase() === 'mkv') return true;
  } catch (e) {
    // If not a valid absolute URL, check regex
  }
  
  return /\.mkv(\?|#|$)/i.test(cleanUrl) || cleanUrl.includes('.mkv');
}

/**
 * Robustly extracts the playable stream URL from any MediaItem or Episode,
 * handling all naming variants (stream_url, streamUrl, url, video_url, link)
 * and normalizing Google Drive / Dropbox / GitHub links to direct streams.
 */
export function extractStreamUrl(item: MediaItem, episodeIndex: number = 0): string {
  if (!item) return '';
  const isSeries = item.type === 'series';
  
  if (isSeries && item.episodes && item.episodes.length > 0) {
    const ep = item.episodes[episodeIndex] || item.episodes[0];
    if (ep) {
      const epUrl = ep.stream_url || (ep as any).streamUrl || (ep as any).url || (ep as any).video_url || (ep as any).link || '';
      if (epUrl && typeof epUrl === 'string' && epUrl.trim()) {
        return normalizeRemoteUrl(epUrl.trim());
      }
    }
  }

  // Fallback to item-level streamUrl / stream_url
  const itemUrl = item.streamUrl || (item as any).stream_url || (item as any).url || (item as any).video_url || (item as any).link || '';
  if (itemUrl && typeof itemUrl === 'string' && itemUrl.trim()) {
    return normalizeRemoteUrl(itemUrl.trim());
  }

  // Check first episode if item was marked as movie or had untyped episode list
  if (item.episodes && item.episodes.length > 0) {
    const firstEp = item.episodes[0];
    const epUrl = firstEp?.stream_url || (firstEp as any)?.streamUrl || (firstEp as any)?.url || '';
    if (epUrl && typeof epUrl === 'string' && epUrl.trim()) {
      return normalizeRemoteUrl(epUrl.trim());
    }
  }

  return '';
}

/**
 * Universal media player launcher:
 * Prioritizes native Media3 ExoPlayer on Android TV, Google TV, Fire TV and Android mobile devices.
 * If running on a desktop web browser without Android bridge, invokes the WebPlayer modal callback.
 */
export function playMediaUniversal(
  item: MediaItem,
  episodeIndex: number = 0,
  resumeTimeSeconds: number = 0,
  fallbackWebPlayer?: (item: MediaItem, epIdx: number, resumeTime: number) => void
): boolean {
  const streamUrl = extractStreamUrl(item, episodeIndex);
  const isSeries = item.type === 'series';
  const currentEpisode = isSeries && item.episodes ? item.episodes[episodeIndex] : undefined;
  
  const androidBridge = typeof window !== 'undefined' ? ((window as any).AndroidExoBridge || (window as any).Android) : null;

  if (androidBridge && streamUrl) {
    const episodeTitle = currentEpisode 
      ? `T${currentEpisode.season || 1} • Ep. ${currentEpisode.episode}: ${currentEpisode.title}`
      : (item.summary ? item.summary.slice(0, 100) : '');
    
    const episodesJson = isSeries && item.episodes && item.episodes.length > 0 
      ? JSON.stringify(item.episodes.map((ep, idx) => ({
          episode: ep.episode || idx + 1,
          season: ep.season || 1,
          title: ep.title || `Episodi ${ep.episode || idx + 1}`,
          stream_url: normalizeRemoteUrl(ep.stream_url || (ep as any).streamUrl || (ep as any).url || ''),
          poster: ep.poster || '',
          runtime: ep.runtime ? `${ep.runtime} min` : ''
        })))
      : '';

    const startPosMs = Math.floor(Math.max(0, resumeTimeSeconds) * 1000);

    // 1. Prefer JSON-based bridge call if available (prevents type mismatch across V8 bridge)
    if (typeof androidBridge.playMediaJson === 'function') {
      try {
        const payload = JSON.stringify({
          mediaId: item.id || `media_${Date.now()}`,
          title: item.title,
          episodeTitle,
          streamUrl,
          posterUrl: item.poster || '',
          episodeNumber: currentEpisode?.episode || (episodeIndex + 1),
          startPositionMs: startPosMs,
          episodesJson,
          currentIndex: episodeIndex
        });
        androidBridge.playMediaJson(payload);
        return true;
      } catch (err) {
        console.warn('playMediaJson call error, falling back to positional playMedia:', err);
      }
    }

    // 2. Try standard multi-argument playMedia
    if (typeof androidBridge.playMedia === 'function') {
      try {
        androidBridge.playMedia(
          item.id || `media_${Date.now()}`,
          item.title,
          episodeTitle,
          streamUrl,
          item.poster || '',
          currentEpisode?.episode || (episodeIndex + 1),
          startPosMs,
          episodesJson,
          episodeIndex
        );
        return true;
      } catch (err) {
        console.warn('Native playMedia bridge call error:', err);
      }
    }
  }

  // If not on Android or bridge threw error, launch web player modal
  if (fallbackWebPlayer) {
    fallbackWebPlayer(item, episodeIndex, resumeTimeSeconds);
  }
  return false;
}

/**
 * Generates an Android Intent URL to open video directly in ExoPlayer or native TV media player
 */
export function getExoPlayerIntentUrl(streamUrl: string, title?: string): string {
  if (!streamUrl) return '';
  const cleanUrl = normalizeRemoteUrl(streamUrl.trim());
  const safeTitle = encodeURIComponent(title || 'AnimeCat Stream');
  
  // Standard Android Intent for ExoPlayer & compatible media players
  return `intent:${cleanUrl}#Intent;type=video/*;package=com.google.android.exoplayer2;S.title=${safeTitle};end`;
}

/**
 * Standard Android Universal Video Intent (works with ExoPlayer, JustPlayer, Nova, etc.)
 */
export function getUniversalAndroidVideoIntent(streamUrl: string, title?: string): string {
  if (!streamUrl) return '';
  const cleanUrl = normalizeRemoteUrl(streamUrl.trim());
  const safeTitle = encodeURIComponent(title || 'AnimeCat Stream');
  return `intent:${cleanUrl}#Intent;action=android.intent.action.VIEW;type=video/*;S.title=${safeTitle};end`;
}

export function copyStreamUrlToClipboard(streamUrl: string): Promise<boolean> {
  if (!navigator?.clipboard) {
    return Promise.resolve(false);
  }
  return navigator.clipboard.writeText(streamUrl).then(() => true).catch(() => false);
}

